/**
 * Live Trade Lifecycle Management (Phase 6) service — talks to the new
 * `/orders`, `/positions`, `/strategies`, `/executions/history` endpoints
 * (see `app.api.v1.positions`). Everything here is about MANAGING an order/
 * position AFTER Phase 4/5 already placed it — this file never previews,
 * confirms, or places a brand-new entry order (that stays exclusively
 * `executionService.ts`'s job).
 *
 * EXECUTION SAFETY: `previewExit`/`previewModifyOrder` never change
 * anything — see the backend `ExitService`/`ModificationService` module
 * docstrings. `confirmExit`/`modifyOrder` are the only two methods in this
 * file that can affect a real broker order, and only after the caller has
 * displayed the corresponding preview and the trader has explicitly
 * confirmed (mirroring `executionService.ts`'s own Preview -> Confirm
 * pattern from Phase 4).
 */

import api from "@/services/api";
import type {
  ActiveStrategiesRead,
  AlgoPerformanceSummaryRead,
  ExecutionHistoryListRead,
  ExitConfirmRequest,
  ExitPreviewRead,
  ExitPreviewRequest,
  ExitResultRead,
  ModifyOrderConfirmRequest,
  ModifyOrderPreviewRead,
  ModifyOrderRequest,
  ModifyOrderResultRead,
  OrderMonitorListRead,
  OrderMonitorRead,
  PositionListRead,
  PositionLegRead,
  ReconcileRequest,
  ReconcileResultRead,
  StrategyPositionRead,
} from "@/types";

export const positionService = {
  // ---- Part 1/3: Order Monitor ----
  async listOrders(): Promise<OrderMonitorListRead> {
    const { data } = await api.get<OrderMonitorListRead>("/orders");
    return data;
  },

  async getOrder(orderId: string): Promise<OrderMonitorRead> {
    const { data } = await api.get<OrderMonitorRead>(`/orders/${orderId}`);
    return data;
  },

  // ---- Part 11/12: Order Modification ----
  async previewModifyOrder(orderId: string, payload: ModifyOrderRequest): Promise<ModifyOrderPreviewRead> {
    const { data } = await api.post<ModifyOrderPreviewRead>(`/orders/${orderId}/modify/preview`, payload);
    return data;
  },

  async modifyOrder(orderId: string, payload: ModifyOrderConfirmRequest): Promise<ModifyOrderResultRead> {
    const { data } = await api.post<ModifyOrderResultRead>(`/orders/${orderId}/modify`, payload);
    return data;
  },

  // ---- Part 5: Position Tracking ----
  async listPositions(): Promise<PositionListRead> {
    const { data } = await api.get<PositionListRead>("/positions");
    return data;
  },

  async getPosition(positionId: string): Promise<PositionLegRead> {
    const { data } = await api.get<PositionLegRead>(`/positions/${positionId}`);
    return data;
  },

  // ---- Part 8: Broker Reconciliation ----
  async reconcile(payload: ReconcileRequest): Promise<ReconcileResultRead> {
    const { data } = await api.post<ReconcileResultRead>("/positions/reconcile", payload);
    return data;
  },

  // ---- Part 6/7/21: Strategy Position View & Active Dashboard ----
  async listActiveStrategies(): Promise<ActiveStrategiesRead> {
    const { data } = await api.get<ActiveStrategiesRead>("/strategies/active");
    return data;
  },

  async getStrategyPosition(executionId: string): Promise<StrategyPositionRead> {
    const { data } = await api.get<StrategyPositionRead>(`/strategies/${executionId}/position`);
    return data;
  },

  // ---- Part 13-20: Exit / Square-off ----
  async previewExit(executionId: string, payload: ExitPreviewRequest): Promise<ExitPreviewRead> {
    const { data } = await api.post<ExitPreviewRead>(`/strategies/${executionId}/exit/preview`, payload);
    return data;
  },

  async confirmExit(executionId: string, payload: ExitConfirmRequest): Promise<ExitResultRead> {
    const { data } = await api.post<ExitResultRead>(`/strategies/${executionId}/exit`, payload);
    return data;
  },

  // ---- Part 22: Execution History ----
  async listExecutionHistory(): Promise<ExecutionHistoryListRead> {
    const { data } = await api.get<ExecutionHistoryListRead>("/executions/history");
    return data;
  },

  // ---- Dashboard "ALGO TRADING" summary (2026-09-10 addition) ----
  // Omitting `months` lets the backend auto-detect the chart's start
  // month from this user's first algo activation (capped to 12 months);
  // pass it explicitly only when a fixed window is actually wanted.
  async getAlgoPerformanceSummary(months?: number): Promise<AlgoPerformanceSummaryRead> {
    const { data } = await api.get<AlgoPerformanceSummaryRead>("/algo-performance/summary", {
      params: months ? { months } : undefined,
    });
    return data;
  },
};

export default positionService;
